import { ChevronDown } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from '~/hooks/useTranslation'
import { Button } from '~/lib/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '~/lib/ui/dropdown-menu'
import useSavedCourses from './SavedCoursesProvider'
import { SavedItemReminder } from '~/components/home/ReminderDialog'
import { cn } from '~/lib/utils'

export function StatusCell({ row }) {
  const { t } = useTranslation('status')
  const { updateCourse, openCompletedCourseDialog, courses } = useSavedCourses()
  const savedCourse = row.original
  const { reminder } = savedCourse
  const [reminderExists, setReminderExists] = React.useState(!!reminder)
  const initialStatus =
    reminder && savedCourse.status === 'saved'
      ? 'reminder_created'
      : savedCourse.status
  const [status, setStatus] = React.useState(initialStatus)

  const handleStatusChange = (e) => {
    updateCourse(savedCourse, { status: e })
    setStatus(e)
    if (e === 'completed') {
      openCompletedCourseDialog(savedCourse)
    }
  }

  React.useEffect(() => {
    setStatus(initialStatus)
  }, [savedCourse, initialStatus, courses])

  const statusColors = {
    saved: 'bg-[#ff824d] hover:bg-[#ff824d]',
    reminder_created: 'bg-[#FFE082] hover:bg-[#FFE082]',
    started: 'bg-[#5CA9E6] hover:bg-[#5CA9E6]',
    completed: 'bg-[#65E083] hover:bg-[#65E083]',
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex flex-row items-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="default"
              className={cn(
                'flex flex-row gap-2 rounded-full border border-[#B9B9B9] text-black hover:bg-opacity-60 sm:px-4 sm:text-base',
                statusColors[status],
              )}
              data-status={status}
            >
              <div className="">{t(status)}</div>
              <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuRadioGroup
              value={status}
              onValueChange={handleStatusChange}
              className="text-base"
            >
              <DropdownMenuRadioItem value="saved" className="text-base">
                {t('saved')}
              </DropdownMenuRadioItem>
              {reminder && (
                <DropdownMenuRadioItem
                  value="reminder_created"
                  className="text-base"
                >
                  {t('reminder_created')}
                </DropdownMenuRadioItem>
              )}
              <DropdownMenuRadioItem value="started" className="text-base">
                {t('started')}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="completed" className="text-base">
                {t('completed')}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {status === 'saved' && !reminderExists && (
        <NotStarted
          savedCourse={savedCourse}
          setStatus={setStatus}
          setReminder={setReminderExists}
        />
      )}
    </div>
  )
}

const NotStarted = ({ savedCourse, setStatus, setReminder }) => {
  const { t } = useTranslation('status')
  const { candidateId } = useSavedCourses()

  const initialText = `Start ${savedCourse.name} course`

  const reminderProps = {
    savedCourse,
    initialText,
    candidateId,
    setStatus,
    setReminder,
  }

  return (
    <div className="flex flex-col gap-1 pr-2">
      <div className="text-normal line-clamp-1 font-bold">
        <SavedItemReminder {...reminderProps}>
          <Button variant="link">{t('set_reminder')}</Button>
        </SavedItemReminder>
      </div>
    </div>
  )
}
